
import { createContext, useContext, useEffect, useState, useCallback } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/lib/auth/auth-context"
import type { Transaction } from "@/lib/types"
import { fetchTransactions, calculateTotals, fetchTransactionsByMonth } from "@/lib/services/transactions"
import type { TransactionTotals, TransactionsByMonth } from "@/lib/services/transactions"

interface TransactionContextType {
  transactions: Transaction[]
  totals: TransactionTotals
  transactionsByMonth: TransactionsByMonth
  loading: boolean
  refreshTransactions: () => Promise<void>
}

const TransactionContext = createContext<TransactionContextType>({
  transactions: [],
  totals: { income: 0, expenses: 0, balance: 0 },
  transactionsByMonth: {},
  loading: true,
  refreshTransactions: async () => {},
})

export function TransactionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [totals, setTotals] = useState<TransactionTotals>({ income: 0, expenses: 0, balance: 0 })
  const [transactionsByMonth, setTransactionsByMonth] = useState<TransactionsByMonth>({})
  const [loading, setLoading] = useState(true)

  // Fetch all transaction data
  const refreshTransactions = useCallback(async () => {
    console.log('🔄 [CONTEXT] refreshTransactions called', {
      timestamp: new Date().toISOString(),
      hasUser: !!user,
      userId: user?.id
    })

    if (!user?.id) {
      console.log('⚠️ [CONTEXT] No user found, clearing transaction data')
      setTransactions([])
      setTotals({ income: 0, expenses: 0, balance: 0 })
      setTransactionsByMonth({})
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      console.log('📥 [CONTEXT] Fetching transactions for user:', user.id)

      const [allTransactions, calculatedTotals, groupedTransactions] = await Promise.all([
        fetchTransactions(user.id),
        calculateTotals(user.id),
        fetchTransactionsByMonth(user.id)
      ])

      console.log('✅ [CONTEXT] Transactions fetched successfully', {
        timestamp: new Date().toISOString(),
        count: allTransactions.length,
        totals: calculatedTotals
      })

      setTransactions(allTransactions)
      setTotals(calculatedTotals)
      setTransactionsByMonth(groupedTransactions)
    } catch (error) {
      console.error('❌ [CONTEXT] Error refreshing transactions:', error)
      console.error('❌ [CONTEXT] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        userId: user.id,
        timestamp: new Date().toISOString()
      })

      // Set empty data on error to show "No transactions" instead of infinite loading
      setTransactions([])
      setTotals({ income: 0, expenses: 0, balance: 0 })
      setTransactionsByMonth({})
    } finally {
      console.log('🏁 [CONTEXT] Setting loading to false')
      setLoading(false)
    }
  }, [user?.id])

  // Initial load
  useEffect(() => {
    refreshTransactions()
  }, [refreshTransactions])

  // Supabase Realtime subscriptions
  useEffect(() => {
    if (!user?.id) {
      console.log('⚠️ [REALTIME] Skipping subscription setup - no user')
      return
    }

    console.log('🔌 [REALTIME] Setting up Supabase Realtime subscriptions', {
      timestamp: new Date().toISOString(),
      userId: user.id
    })

    // Create a channel for listening to changes
    const channel = supabase
      .channel('transaction-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'user_expenses',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📥 [REALTIME] Expense change detected', {
            timestamp: new Date().toISOString(),
            event: payload.eventType,
            data: payload.new || payload.old
          })
          refreshTransactions()
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'user_incomes',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📥 [REALTIME] Income change detected', {
            timestamp: new Date().toISOString(),
            event: payload.eventType,
            data: payload.new || payload.old
          })
          refreshTransactions()
        }
      )
      .subscribe((status) => {
        console.log('🔌 [REALTIME] Subscription status:', {
          timestamp: new Date().toISOString(),
          status,
          userId: user.id
        })

        if (String(status) === 'SUBSCRIPTION_ERROR' || String(status) === 'CHANNEL_ERROR') {
          console.error('❌ [REALTIME] Subscription error - realtime updates will not work', status)
        }
      })

    // Cleanup subscription on unmount
    return () => {
      console.log('🔌 [REALTIME] Cleaning up subscriptions', {
        timestamp: new Date().toISOString()
      })
      supabase.removeChannel(channel)
    }
  }, [user?.id, refreshTransactions])

  return (
    <TransactionContext.Provider
      value={{
        transactions,
        totals,
        transactionsByMonth,
        loading,
        refreshTransactions,
      }}
    >
      {children}
    </TransactionContext.Provider>
  )
}

export const useTransactions = () => {
  const context = useContext(TransactionContext)
  if (!context) {
    throw new Error("useTransactions must be used within TransactionProvider")
  }
  return context
}
